/**
 * InfoModal Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A lightweight modal component for displaying informational content with:
 * - Viewport-relative positioning for optimal viewing
 * - HTML content parsing for rich text display
 * - Keyboard navigation (Escape key support)
 * - Small modal variant for compact displays
 * - Smooth positioning and centering
 * - Portal rendering to document body
 * 
 * @param {string} data - HTML content to display in the modal
 * @param {string} title - Title for the modal header
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onClose - Callback function when modal is closed
 * @param {boolean} small - Whether to display in small modal mode
 * @returns {JSX.Element|null} Modal component or null if not shown
 */
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Parser from 'html-react-parser';
import apiHelper from '../utils/apiHelper';
import { useProfile } from '../contexts/ProfileContext';
import './InfoModal.css';   

/**
 * InfoModal component for displaying informational content
 * with viewport-centered positioning and keyboard navigation
 */
function InfoModal({ data, title, show, onClose, small }) {
    const { profile } = useProfile();
    const modalRef = useRef(null);

    useEffect(() => {
        if (show && profile?.email) {
            apiHelper.logModalOpen(profile.email, 'InfoModal', title || 'Info', 'Info');
        }
    }, [show, profile?.email]);

    // No manual positioning needed - CSS handles centering with flexbox
    useEffect(() => {
        if (show && modalRef.current) {
            const modalElement = modalRef.current;
            // Only set z-index, let CSS handle positioning
            modalElement.style.zIndex = '2147483647';
        }
    }, [show]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    if (!show) return null;

    const modalClass = small ? 'small-modal' : '';

    const modalContent = (
        <div ref={modalRef} className={`info-modal ${modalClass}`}>
            <div className="info-modal-content">
                <div className="modal-header">
                    <h2 id="modalTitle">{title}</h2>
                    <span
                        className="modalclose pointer fas fa-times"
                        onClick={onClose}
                    ></span>
                </div>
                <div className="modal-body">
                    <div className="summary-content summary">
                        {Parser(data)}
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(
        modalContent,
        document.body
    );
}

export default InfoModal;
